import { belvedereContract } from "../data/belvedereContractData";
import { royalTulipTbilisiContract } from "../data/royalTulipTbilisiContractData";
import { resolveCampaignContract, resolveCampaignFlightDestination } from "../data/destinationCampaignHotels";
import {
  campaignBoardDisplayLabelEn,
  campaignOfferDisplayName,
  stripCampaignBoardFromRoomName,
} from "../utils/campaignHotelProfileRooms";
import {
  BELVEDERE_DEFAULT_ROOM_IMAGE,
  getBelvedereRoomImageByCode,
  getBelvedereRoomImageUrl,
  withBelvederePublicAsset,
} from "../data/belvedereRoomImages";
import { BELVEDERE_HERO_IMAGE, BELVEDERE_GALLERY_IMAGES } from "../data/belvedereHotelMedia";
import {
  ROYAL_TULIP_HERO_IMAGE,
  ROYAL_TULIP_LOCAL_GALLERY_IMAGES,
} from "../data/royalTulipTbilisiHotelMedia";
import { resolveRoyalTulipOfferRoomImage } from "../data/royalTulipTbilisiRoomImages";
import {
  buildBelvedereCampaignFlightLegRows,
  campaignFlightServesDestination,
  formatBelvedereCampaignAirportLabel,
  normalizeCampaignAirportCode,
  reconcileRoomPackageSharesToTotal,
} from "./belvedereFlightSearch";
import {
  formatCampaignRoomOccupancy,
  formatCampaignSearchOccupancy,
  getStayNights,
  resolveBelvedereCustomerPassengerPricing,
} from "./belvederePricing";
import {
  buildCampaignBaggageQuoteItemsFromLdsSupplement,
} from "./belvedereCampaignBaggage";
import { resolveFlightBaggageDisplayState } from "../Components/FlightBookingFlowMockup/adapters/flightBaggageStepPolicy";
import { getDynamicOrder } from "./dynamicService";
import { getUserDetails, isAdminUser, resolveSessionAgentId } from "./authService";
import { buildCustomerQuoteAgencyBranding } from "../utils/buildCustomerQuoteAgencyBranding";
import { buildAgentPublicOfferBranding } from "../utils/buildAgentPublicOfferBranding";
import { airlineDisplayNameFromCode, buildAirlineNameMap } from "../utils/airlineNameFromCode";

export const BELVEDERE_CAMPAIGN_SLUG = "royal-belvedere-2026";
const BELVEDERE_CAMPAIGN_PUBLIC_PATH = "/he/campaigns/royal-belvedere-2026";
const ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH = "/he/campaigns/royal-tulip-tbilisi-2026";
const ROYAL_TULIP_CAMPAIGN_SLUG = royalTulipTbilisiContract.hotelCode;

function campaignPublicPathForSlug(slug) {
  if (String(slug || "").trim() === ROYAL_TULIP_CAMPAIGN_SLUG) {
    return ROYAL_TULIP_CAMPAIGN_PUBLIC_PATH;
  }
  return BELVEDERE_CAMPAIGN_PUBLIC_PATH;
}

function buildOfferPaymentPath(orderId, campaignPublicPath = BELVEDERE_CAMPAIGN_PUBLIC_PATH) {
  const oid = encodeURIComponent(String(orderId || "").trim());
  if (!oid) return null;
  const params = new URLSearchParams();
  params.set("returnTo", campaignPublicPath || BELVEDERE_CAMPAIGN_PUBLIC_PATH);
  params.set("source", "belvedere_campaign");
  const prefix = campaignPublicPath || BELVEDERE_CAMPAIGN_PUBLIC_PATH;
  return `${prefix}/payment/${oid}?${params.toString()}`;
}

const CAMPAIGN_PENDING_STORAGE_PREFIX = "belvedere_campaign_pending:";
const CHECKOUT_CTX_STORAGE_PREFIX = "belvedere_campaign_checkout_ctx:";

function coerceObject(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function coerceClientsInfo(order) {
  return coerceObject(order?.clientsinfo) || coerceObject(order?.clientsInfo) || {};
}

function resolveOrderCampaignContract(order, fallbackContext = null) {
  const ci = coerceClientsInfo(order);
  const selection = coerceObject(ci.campaign_selection) || {};
  return (
    resolveCampaignContract(
      fallbackContext?.hotel?.hotelCode ||
        selection.hotel_code ||
        ci.campaign ||
        ci.hotel_code,
    ) ||
    resolveCampaignContract(fallbackContext?.hotel) ||
    null
  );
}

export function markBelvedereCampaignPaymentPending(orderId) {
  if (typeof sessionStorage === "undefined") return;
  const oid = String(orderId || "").trim();
  if (!oid) return;
  try {
    sessionStorage.setItem(`${CAMPAIGN_PENDING_STORAGE_PREFIX}${oid}`, "1");
  } catch {
    /* ignore */
  }
}

export function clearBelvedereCampaignPaymentPending(orderId) {
  if (typeof sessionStorage === "undefined") return;
  const oid = String(orderId || "").trim();
  if (!oid) return;
  try {
    sessionStorage.removeItem(`${CAMPAIGN_PENDING_STORAGE_PREFIX}${oid}`);
  } catch {
    /* ignore */
  }
}

export function isBelvedereCampaignPaymentPending(orderId) {
  if (typeof sessionStorage === "undefined") return false;
  const oid = String(orderId || "").trim();
  if (!oid) return false;
  try {
    return sessionStorage.getItem(`${CAMPAIGN_PENDING_STORAGE_PREFIX}${oid}`) === "1";
  } catch {
    return false;
  }
}

export function persistBelvedereCampaignCheckoutContext(orderId, context) {
  if (typeof sessionStorage === "undefined" || !context) return;
  const oid = String(orderId || "").trim();
  if (!oid) return;
  try {
    sessionStorage.setItem(`${CHECKOUT_CTX_STORAGE_PREFIX}${oid}`, JSON.stringify(context));
  } catch {
    /* ignore */
  }
}

export function readBelvedereCampaignCheckoutContext(orderId) {
  if (typeof sessionStorage === "undefined") return null;
  const oid = String(orderId || "").trim();
  if (!oid) return null;
  try {
    const raw = sessionStorage.getItem(`${CHECKOUT_CTX_STORAGE_PREFIX}${oid}`);
    return raw ? coerceObject(raw) : null;
  } catch {
    return null;
  }
}

export function isBelvedereCampaignOrder(order, orderId = null) {
  const ci = coerceClientsInfo(order);
  const slug = String(ci.campaign || ci.campaign_slug || "").trim();
  if (slug === BELVEDERE_CAMPAIGN_SLUG || slug === ROYAL_TULIP_CAMPAIGN_SLUG) return true;
  const oid = String(orderId || order?.id || "").trim();
  return oid ? isBelvedereCampaignPaymentPending(oid) : false;
}

/** Dynamic package row saved via bookFlightOnly before LDS PROPOSAL exists. */
export function isBelvedereCampaignDynamicOrder(order) {
  if (!order || typeof order !== "object") return false;
  const ci = coerceClientsInfo(order);
  const slug = String(ci.campaign || ci.campaign_slug || "").trim();
  if (slug === BELVEDERE_CAMPAIGN_SLUG || slug === ROYAL_TULIP_CAMPAIGN_SLUG) {
    return true;
  }
  if (
    ci.product_type === "campaign_hotel_only" ||
    ci.product_type === "campaign_hotel_quote" ||
    ci.product_type === "campaign_hotel_flight_quote"
  ) {
    return true;
  }
  const flights = order.flights || {};
  const linked = flights.linkedflights || flights.linkedFlights || {};
  if (linked && typeof linked === "object" && linked.campaign_hotel_only === true) {
    return true;
  }
  const extended = flights.extended || {};
  return extended.belvedere_campaign_hotel_only === true;
}

export function resolveOrderCampaignSlug(order, fallbackContext = null) {
  const ci = coerceClientsInfo(order);
  const selection = coerceObject(ci.campaign_selection) || {};
  const fromFields = String(
    ci.campaign ||
      ci.campaign_slug ||
      selection.hotel_code ||
      ci.hotel_code ||
      fallbackContext?.hotel?.hotelCode ||
      "",
  ).trim();
  if (fromFields === ROYAL_TULIP_CAMPAIGN_SLUG || fromFields === BELVEDERE_CAMPAIGN_SLUG) {
    return fromFields;
  }
  const contract = resolveOrderCampaignContract(order, fallbackContext);
  const fromContract = String(contract?.hotelCode || "").trim();
  if (fromContract === ROYAL_TULIP_CAMPAIGN_SLUG) return ROYAL_TULIP_CAMPAIGN_SLUG;
  if (fromContract === BELVEDERE_CAMPAIGN_SLUG) return BELVEDERE_CAMPAIGN_SLUG;
  return fromFields || BELVEDERE_CAMPAIGN_SLUG;
}

/**
 * Belvedere campaign checkout navigates to campaign payment/:uuid before LDS row exists.
 * Load the saved dynamic package instead of getLdsOrder.
 */
export function isBelvedereCampaignPaymentRoute(orderId, location) {
  const oid = String(orderId || "").trim();
  if (!oid) return false;
  if (isBelvedereCampaignPaymentPending(oid)) return true;
  const stateSource = String(location?.state?.source || "").trim();
  if (stateSource === "belvedere_campaign") return true;
  try {
    const params = new URLSearchParams(location?.search || "");
    if (params.get("source") === "belvedere_campaign") return true;
  } catch {
    /* ignore */
  }
  const path = String(location?.pathname || "");
  // Match 2026 campaign payment (and legacy year paths) so UUID never hits getLdsOrder.
  if (/\/campaigns\/(royal-belvedere|royal-tulip-tbilisi)-20\d{2}\/payment\//i.test(path)) {
    return true;
  }
  return false;
}

/** Voucher + confirmation email eligibility (bank transfer submitted or paid). */
export function canDownloadBelvedereCampaignHotelVoucher(order) {
  if (!isBelvedereCampaignDynamicOrder(order)) return false;
  const paid = Boolean(order?.paid);
  const total = Number(order?.total_price ?? order?.totalPrice ?? 0);
  const sumpaid = Number(order?.sumpaid ?? order?.sumPaid ?? 0);
  if (paid || (total > 0 && sumpaid >= total - 0.03)) return true;
  const flights = order?.flights || {};
  if (flights.bank_transfer || flights.bankTransfer) return true;
  const ci = coerceClientsInfo(order);
  const hm = ci.hrFlightPackage || ci.hr_flight_package || {};
  if (String(hm.payment_method || "").toLowerCase().replace(/-/g, "_") === "bank_transfer") {
    return true;
  }
  return String(ci.payment_method || "").toLowerCase().replace(/-/g, "_") === "bank_transfer";
}

function formatCampaignDateLabel(raw) {
  const value = String(raw || "").trim();
  if (!value) return "—";
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return `${dmy[1].padStart(2, "0")}/${dmy[2].padStart(2, "0")}/${dmy[3]}`;
  return value;
}

export function resolveBelvedereCampaignSupplierStatus(order) {
  const flights = order?.flights || {};
  const orderResponse = coerceObject(flights.order_response);
  const paid = Boolean(order?.paid);
  const bankTransfer = Boolean(flights.bank_transfer || flights.bankTransfer);

  if (!paid && !bankTransfer) {
    return {
      statusLabel: "ממתין לתשלום",
      statusHint: "ההזמנה נשמרה במערכת ולא נשלחה לספק עד להשלמת תשלום.",
      proposalSent: false,
    };
  }

  const opStatus = String(
    orderResponse?.opStatus?.status ?? orderResponse?.opstatus ?? orderResponse?.OPStatus ?? "",
  )
    .trim()
    .toUpperCase();

  const supplierAcknowledged =
    Boolean(orderResponse?.reservation) ||
    opStatus === "SUCCESS" ||
    Boolean(order?.flight_booked);

  if (supplierAcknowledged || bankTransfer) {
    return {
      statusLabel: "נשלח לספק לאישור",
      statusHint: "ההזמנה ממתינה לאישור סופי מהספק.",
      proposalSent: true,
    };
  }

  return {
    statusLabel: "ממתין לאישור סופי מול הספק",
    statusHint: "התשלום התקבל; אישור הספק בטיפול.",
    proposalSent: false,
  };
}

export function resolveBelvedereCampaignOrderSummary(order, fallbackContext = null) {
  const ci = coerceClientsInfo(order);
  const selection = coerceObject(ci.campaign_selection) || {};
  const flights = order?.flights || {};
  const linkedFlights = flights.linkedflights || flights.linkedFlights || null;
  const hotelOnlyStub = Boolean(
    linkedFlights?.campaign_hotel_only ||
      flights?.extended?.belvedere_campaign_hotel_only ||
      ci.product_type === "campaign_hotel_only" ||
      ci.product_type === "campaign_hotel_quote",
  );
  const checkIn =
    selection.arrival_date ||
    order?.check_in ||
    order?.checkIn ||
    flights.dep_date ||
    fallbackContext?.arrivalDate;
  const checkOut =
    selection.departure_date ||
    order?.check_out ||
    order?.checkOut ||
    flights.ret_date ||
    fallbackContext?.departureDate;

  const pseudoDeal =
    linkedFlights && !hotelOnlyStub
      ? {
          linkedFlights,
          linkedflights: linkedFlights,
          depDate: flights.dep_date || checkIn,
          retDate: flights.ret_date || checkOut,
        }
      : null;

  const legRows = pseudoDeal ? buildBelvedereCampaignFlightLegRows(pseudoDeal) : [];
  const rawRoomName =
    selection.selected_room?.name ||
    ci.hotel_room ||
    fallbackContext?.selectedRoom?.name ||
    null;
  const roomName = rawRoomName
    ? campaignOfferDisplayName(
        selection.selected_room || fallbackContext?.selectedRoom || { name: rawRoomName },
        stripCampaignBoardFromRoomName(rawRoomName, rawRoomName),
      )
    : null;
  const contract = resolveOrderCampaignContract(order, fallbackContext);
  const campaignSlug = resolveOrderCampaignSlug(order, fallbackContext);
  const expectedFlightDest =
    resolveCampaignFlightDestination(contract?.hotelCode || campaignSlug) || null;
  const actualFlightDest = normalizeCampaignAirportCode(legRows[0]?.destinationCode);
  const routeDest = actualFlightDest || expectedFlightDest || "HER";

  return {
    orderId: String(order?.id || fallbackContext?.orderId || "").trim() || null,
    hotelName:
      fallbackContext?.hotel?.name ||
      contract?.hotelNameHe ||
      contract?.hotelName ||
      belvedereContract.hotelNameHe ||
      belvedereContract.hotelName,
    roomName,
    checkInLabel: formatCampaignDateLabel(checkIn),
    checkOutLabel: formatCampaignDateLabel(checkOut),
    hasFlights: Boolean(!hotelOnlyStub && (legRows.length || fallbackContext?.selectedFlights?.length)),
    hotelOnly: hotelOnlyStub,
    legRows,
    campaignSlug,
    routeLabel:
      legRows.length > 0
        ? `${legRows[0]?.originCode || "—"} ↔ ${legRows[0]?.destinationCode || "—"}`
        : selection.flight_origin || fallbackContext?.flightOrigin
          ? `${selection.flight_origin || fallbackContext.flightOrigin} ↔ ${routeDest}`
          : null,
    supplierStatus: resolveBelvedereCampaignSupplierStatus(order),
  };
}

/**
 * View-model for the public Belvedere campaign customer offer page (hotel + flights).
 */
function isGenericMultiRoomPackageLabel(name) {
  return /^חבילת\s+\d+\s+חדרים$/.test(String(name || "").trim());
}

function isIndexOnlyRoomLabel(name) {
  return /^חדר\s+\d+$/i.test(String(name || "").trim());
}

function resolveOfferRoomImage(roomId, roomCode, imageMeta = {}) {
  if (imageMeta.campaignSlug === ROYAL_TULIP_CAMPAIGN_SLUG) {
    return resolveRoyalTulipOfferRoomImage({
      roomId,
      roomCode,
      name: imageMeta.name,
      imageKey: imageMeta.imageKey,
    });
  }
  return (
    getBelvedereRoomImageUrl(roomId) ||
    getBelvedereRoomImageByCode(roomCode) ||
    withBelvederePublicAsset(BELVEDERE_DEFAULT_ROOM_IMAGE)
  );
}

/** Empty / index-only / generic package labels — not a specific room type. */
function isPlaceholderRoomLabel(name) {
  const value = String(name || "").trim();
  if (!value) return true;
  if (isGenericMultiRoomPackageLabel(value)) return true;
  return isIndexOnlyRoomLabel(value);
}

/** Specific Hebrew room-type name (not package aggregate / not "חדר N"). */
function isStrongRoomLabel(name) {
  const value = String(name || "").trim();
  if (!value) return false;
  return !isPlaceholderRoomLabel(value);
}

/**
 * Per-room display name only — never package aggregate "חבילת N חדרים" or "חדר N".
 */
function resolveStrongRoomLabel(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (isStrongRoomLabel(value)) return value;
  }
  return null;
}

/** Parse combo ids like "4-5" / "7-7" into contract room codes. */
function parseComboRoomCodes(...candidates) {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const codes = candidate
        .map((code) => Number(code))
        .filter((code) => Number.isFinite(code));
      if (codes.length > 0) return codes;
      continue;
    }
    const raw = String(candidate || "").trim();
    if (!raw || !/^\d+(?:-\d+)+$/.test(raw)) continue;
    return raw.split("-").map((part) => Number(part));
  }
  return [];
}

function pickRawRoomName(room) {
  if (!room || typeof room !== "object") return null;
  return (
    room.name ||
    room.nameHe ||
    room.displayNameHe ||
    room.roomName ||
    room.roomNameHe ||
    room.title ||
    null
  );
}

function campaignContractRoomTypes(campaignSlug) {
  const tulip = Array.isArray(royalTulipTbilisiContract?.roomTypes)
    ? royalTulipTbilisiContract.roomTypes
    : [];
  const belvedere = Array.isArray(belvedereContract?.roomTypes)
    ? belvedereContract.roomTypes
    : [];
  if (campaignSlug === ROYAL_TULIP_CAMPAIGN_SLUG) return tulip;
  if (campaignSlug === BELVEDERE_CAMPAIGN_SLUG) return belvedere;
  return [...belvedere, ...tulip];
}

function resolveContractRoomMeta(roomId, roomCode, campaignSlug) {
  const types = campaignContractRoomTypes(campaignSlug);
  if (roomId) {
    const byId = types.find((row) => row.id === roomId);
    if (byId) return byId;
  }
  if (roomCode != null && Number.isFinite(Number(roomCode))) {
    return types.find((row) => Number(row.code) === Number(roomCode)) || null;
  }
  return null;
}

function extractRoomIdentity(room, packageRoom = {}, campaignSlug = null) {
  const nestedRoom = room?.room && typeof room.room === "object" ? room.room : null;
  const roomId =
    room?.roomId ||
    room?.id ||
    nestedRoom?.id ||
    packageRoom.roomId ||
    packageRoom.id ||
    null;
  const rawCode =
    room?.roomCode != null
      ? room.roomCode
      : room?.code != null
        ? room.code
        : nestedRoom?.code != null
          ? nestedRoom.code
          : packageRoom.roomCode != null
            ? packageRoom.roomCode
            : packageRoom.code != null
              ? packageRoom.code
              : null;
  const roomCode = rawCode != null && rawCode !== "" ? Number(rawCode) : null;
  const codeOk = Number.isFinite(roomCode) ? roomCode : null;
  const contractRoom = resolveContractRoomMeta(roomId, codeOk, campaignSlug);
  const resolvedId = roomId || contractRoom?.id || null;
  const resolvedCode =
    codeOk != null ? codeOk : contractRoom?.code != null ? Number(contractRoom.code) : null;
  const rawName = pickRawRoomName(room) || pickRawRoomName(nestedRoom);
  const packageName = pickRawRoomName(packageRoom);
  const name =
    (isStrongRoomLabel(rawName) && rawName) ||
    contractRoom?.name ||
    contractRoom?.nameHe ||
    (isStrongRoomLabel(packageName) && packageName) ||
    null;
  return {
    roomId: resolvedId,
    roomCode: resolvedCode,
    name,
    imageKey:
      room?.imageKey ||
      nestedRoom?.imageKey ||
      packageRoom.imageKey ||
      contractRoom?.imageKey ||
      null,
    contractRoom,
  };
}

function hasRoomTypeIdentity(room) {
  if (!room || typeof room !== "object") return false;
  if (room.roomId || room.id) return true;
  if (room.roomCode != null || room.code != null) return true;
  if (room.room && typeof room.room === "object") {
    if (room.room.id || room.room.code != null) return true;
    if (isStrongRoomLabel(pickRawRoomName(room.room))) return true;
  }
  return isStrongRoomLabel(pickRawRoomName(room));
}

function resolvePersistedPackageWeightedIls(row) {
  const raw = Number(row?.packageWeightedIls ?? row?.package_weighted_ils);
  return Number.isFinite(raw) && raw > 0 ? Math.round(raw * 100) / 100 : null;
}

function normalizeBreakdownRoomRow(row) {
  if (!row || typeof row !== "object") return null;
  const nested = row.room && typeof row.room === "object" ? row.room : null;
  const roomCode =
    row.roomCode != null
      ? row.roomCode
      : row.code != null
        ? row.code
        : nested?.code != null
          ? nested.code
          : null;
  const roomId = row.roomId || row.id || nested?.id || null;
  const name =
    pickRawRoomName(row) ||
    row.roomNameHe ||
    pickRawRoomName(nested) ||
    null;
  if (
    roomCode == null &&
    !roomId &&
    !isStrongRoomLabel(name) &&
    !row.occupancyLabel
  ) {
    return null;
  }
  const packageWeightedIls = resolvePersistedPackageWeightedIls(row);
  return {
    roomCode,
    roomId,
    name,
    nameHe: row.nameHe || nested?.nameHe || name,
    board: row.board || nested?.board || null,
    chargeType: row.chargeType || nested?.chargeType || null,
    occupancyLabel: row.occupancyLabel || null,
    ...(packageWeightedIls != null ? { packageWeightedIls } : {}),
  };
}

function resolveRoomSourceRows(selectedRoom, selection) {
  const nestedRooms = Array.isArray(selectedRoom?.rooms) ? selectedRoom.rooms : [];
  if (nestedRooms.some((room) => hasRoomTypeIdentity(room))) {
    return nestedRooms;
  }
  const breakdown =
    (Array.isArray(selection?.room_breakdown) && selection.room_breakdown) ||
    (Array.isArray(selection?.roomBreakdown) && selection.roomBreakdown) ||
    (Array.isArray(selectedRoom?.room_breakdown) && selectedRoom.room_breakdown) ||
    (Array.isArray(selectedRoom?.roomBreakdown) && selectedRoom.roomBreakdown) ||
    [];
  const fromBreakdown = breakdown.map(normalizeBreakdownRoomRow).filter(Boolean);
  if (fromBreakdown.some((room) => hasRoomTypeIdentity(room))) {
    return fromBreakdown;
  }

  const comboCodes = parseComboRoomCodes(
    selectedRoom?.comboId,
    selectedRoom?.combo_id,
    selectedRoom?.offerId,
    selectedRoom?.id,
    selectedRoom?.componentCodes,
    selectedRoom?.component_codes,
    selection?.combo_id,
    selection?.comboId,
  );
  if (comboCodes.length > 1) {
    return comboCodes.map((roomCode) => {
      const contractRoom = resolveContractRoomMeta(null, roomCode);
      return {
        roomCode,
        roomId: contractRoom?.id || null,
        name: contractRoom?.name || contractRoom?.nameHe || null,
        nameHe: contractRoom?.nameHe || contractRoom?.name || null,
      };
    });
  }

  return nestedRooms;
}

function resolveRoomOccupancyLabel(room, searchOccupancy) {
  if (searchOccupancy) return formatCampaignRoomOccupancy(searchOccupancy);
  if (room?.occupancyLabel) return room.occupancyLabel;
  if (
    room &&
    (room.adults != null || room.children != null || Array.isArray(room.childAges))
  ) {
    return formatCampaignRoomOccupancy(room);
  }
  return null;
}

/**
 * Per-room rows for the customer offer stay card (name, occupancy, image).
 * Prefers selected_room.rooms / room_breakdown / comboId codes.
 * Uniform multi-room may reuse package-level roomId/code/name.
 * Never uses "חבילת N חדרים" as a per-room name.
 */
export function buildBelvedereOfferRoomDetails(
  selection,
  fallbackRoomName = null,
  options = {},
) {
  const campaignSlug =
    options.campaignSlug ||
    selection?.hotel_code ||
    selection?.campaign_slug ||
    null;
  const imageMeta = { campaignSlug };
  const searchRooms = Array.isArray(selection?.rooms) ? selection.rooms : [];
  const selectedRoom = selection?.selected_room || {};
  const sourceRooms = resolveRoomSourceRows(selectedRoom, selection);
  const packageIdentity = extractRoomIdentity(
    selectedRoom,
    {
      name: pickRawRoomName(selectedRoom),
      nameHe: selectedRoom.nameHe,
      displayNameHe: selectedRoom.displayNameHe,
    },
    campaignSlug,
  );
  // Only reuse package-level name when it is a real room type (uniform multi-room).
  const uniformPackageName =
    (packageIdentity.roomId || packageIdentity.roomCode != null) &&
    isStrongRoomLabel(packageIdentity.name)
      ? packageIdentity.name
      : isStrongRoomLabel(fallbackRoomName) &&
          (packageIdentity.roomId || packageIdentity.roomCode != null)
        ? fallbackRoomName
        : null;

  const usableNested = sourceRooms.filter((room) => hasRoomTypeIdentity(room));
  if (usableNested.length > 0) {
    const rows =
      sourceRooms.length >= usableNested.length ? sourceRooms : usableNested;
    return rows.map((room, index) => {
      const identity = extractRoomIdentity(
        room,
        {
          roomId: packageIdentity.roomId,
          roomCode: packageIdentity.roomCode,
          name: uniformPackageName,
          imageKey: packageIdentity.imageKey,
        },
        campaignSlug,
      );
      const occupancy = searchRooms[index];
      const name = resolveStrongRoomLabel(identity.name, uniformPackageName);
      const packageWeightedIls = resolvePersistedPackageWeightedIls(room);
      return {
        roomIndex: index + 1,
        name,
        occupancyLabel: resolveRoomOccupancyLabel(room, occupancy),
        imageUrl: resolveOfferRoomImage(identity.roomId, identity.roomCode, {
          ...imageMeta,
          name,
          imageKey: identity.imageKey,
        }),
        roomId: identity.roomId,
        roomCode: identity.roomCode,
        packageWeightedIls,
      };
    });
  }

  const count = Math.max(
    Number(selectedRoom.quantity) || 0,
    searchRooms.length || 0,
    sourceRooms.length || 0,
    uniformPackageName || packageIdentity.roomId || packageIdentity.roomCode != null
      ? 1
      : 0,
  );
  if (count <= 0) return [];

  const imageUrl = resolveOfferRoomImage(
    packageIdentity.roomId,
    packageIdentity.roomCode,
    {
      ...imageMeta,
      name: uniformPackageName,
      imageKey: packageIdentity.imageKey,
    },
  );

  return Array.from({ length: count }, (_, index) => ({
    roomIndex: index + 1,
    name: uniformPackageName,
    occupancyLabel: resolveRoomOccupancyLabel(
      sourceRooms[index] || null,
      searchRooms[index],
    ),
    imageUrl,
    roomId: packageIdentity.roomId,
    roomCode: packageIdentity.roomCode,
    packageWeightedIls: resolvePersistedPackageWeightedIls(sourceRooms[index] || null),
  }));
}

function formatOfferRoomNameSummary(roomDetails, fallbackName) {
  if (!Array.isArray(roomDetails) || roomDetails.length === 0) {
    return resolveStrongRoomLabel(fallbackName);
  }
  const strongNames = roomDetails
    .map((row) => row.name)
    .filter((name) => isStrongRoomLabel(name));
  if (strongNames.length === 0) {
    return resolveStrongRoomLabel(fallbackName);
  }
  const unique = [...new Set(strongNames)];
  if (unique.length === 1) {
    return roomDetails.length > 1
      ? `${unique[0]} · ${roomDetails.length} חדרים`
      : unique[0];
  }
  return strongNames.join(" · ");
}

function resolveBelvedereCampaignOfferAgentBranding(order, ci) {
  const snap = coerceObject(ci?.agent_branding);
  if (snap) {
    const branding = {
      logoUrl: snap.logoUrl || snap.logo_url || null,
      agencyName: snap.agencyName || snap.companyName || snap.company_name || null,
      agentName: snap.agentName || snap.contactName || snap.contact_name || null,
      phone: snap.phone || null,
      email: snap.email || null,
      whatsapp: snap.whatsapp || null,
    };
    if (Object.values(branding).some(Boolean)) return branding;
  }
  const fromOrder = buildCustomerQuoteAgencyBranding(order);
  if (fromOrder) {
    return {
      logoUrl: fromOrder.logoUrl,
      agencyName: fromOrder.companyName,
      agentName: fromOrder.contactName,
      phone: fromOrder.phone,
      email: fromOrder.email,
      whatsapp: null,
    };
  }
  return buildAgentPublicOfferBranding(order?.agent) || null;
}

export function hydrateBelvedereCampaignCustomerOffer(order) {
  if (!order || !isBelvedereCampaignDynamicOrder(order)) return null;

  const ci = coerceClientsInfo(order);
  const selection = coerceObject(ci.campaign_selection) || {};
  const summary = resolveBelvedereCampaignOrderSummary(order);
  const rooms = Array.isArray(selection.rooms) ? selection.rooms : [];
  const selectedRoom = selection.selected_room || {};
  const contract = resolveOrderCampaignContract(order);
  const campaignSlug = summary.campaignSlug || resolveOrderCampaignSlug(order);
  const boardEn = campaignBoardDisplayLabelEn({
    ...selectedRoom,
    campaignKosher: Boolean(contract?.kosher),
  });
  let roomDetails = buildBelvedereOfferRoomDetails(selection, summary.roomName, {
    campaignSlug,
  });
  const roomName =
    formatOfferRoomNameSummary(roomDetails, summary.roomName) || summary.roomName;
  const roomImage =
    roomDetails[0]?.imageUrl ||
    resolveOfferRoomImage(
      selectedRoom.roomId || null,
      selectedRoom.roomCode != null ? Number(selectedRoom.roomCode) : null,
      {
        campaignSlug,
        name: roomName || selectedRoom.name,
        imageKey: selectedRoom.imageKey,
      },
    );

  const finalSalePriceIls = Number(
    ci.final_sale_price_ils ?? order.total_price ?? order.totalPrice ?? 0,
  );
  if (
    Array.isArray(roomDetails) &&
    roomDetails.length > 1 &&
    Number.isFinite(finalSalePriceIls) &&
    finalSalePriceIls > 0 &&
    roomDetails.some((row) => Number.isFinite(Number(row.packageWeightedIls)))
  ) {
    roomDetails = reconcileRoomPackageSharesToTotal(roomDetails, finalSalePriceIls);
  }
  const agentNetPriceIls = Number(ci.agent_net_price_ils ?? 0);
  const agentAddedCommissionIls = Number(ci.agent_added_commission_ils ?? 0);
  const passengerPricing = ci.passenger_pricing || null;
  const arrivalDate = selection.arrival_date || null;
  const departureDate = selection.departure_date || null;
  const nights =
    selection.nights != null
      ? Number(selection.nights)
      : arrivalDate && departureDate
        ? getStayNights(arrivalDate, departureDate)
        : null;
  const flightOrigin = selection.flight_origin || "TLV";
  const productType =
    selection.product_type ||
    (summary.hasFlights ? "hotel_flight" : "hotel");

  const flightsPayload = order?.flights || {};
  const linkedFlights =
    flightsPayload.linkedflights || flightsPayload.linkedFlights || null;
  const baggageDealLike = linkedFlights
    ? {
        linkedFlights,
        linkedflights: linkedFlights,
        supplement: flightsPayload.supplement || flightsPayload.supplements,
      }
    : null;
  const includedBaggageState = baggageDealLike
    ? resolveFlightBaggageDisplayState(baggageDealLike)
    : { displayState: "no_baggage", includedLines: [] };
  const airlineNameMap = buildAirlineNameMap([]);

  const expectedFlightDest =
    resolveCampaignFlightDestination(
      contract?.hotelCode || summary.campaignSlug || ci.campaign || selection.hotel_code,
    ) || null;
  const actualFlightDest = normalizeCampaignAirportCode(summary.legRows?.[0]?.destinationCode);
  const flightDestination = actualFlightDest || expectedFlightDest || "";
  const flightsMatchCampaign =
    !summary.hasFlights ||
    !expectedFlightDest ||
    campaignFlightServesDestination(
      { destination: actualFlightDest, legRows: summary.legRows || [] },
      expectedFlightDest,
    );

  const flight = summary.hasFlights
    ? {
        legRows: (summary.legRows || []).map((leg) => {
          const code = leg.airlineCode || null;
          const rawName = String(leg.airlineName || "").trim();
          const airlineName =
            rawName && !/^[A-Z0-9]{2,3}$/i.test(rawName)
              ? rawName
              : airlineDisplayNameFromCode(code || rawName, airlineNameMap);
          const flightNumberRaw = String(leg.flightNumber || "").trim();
          const flightNumber = flightNumberRaw
            .replace(new RegExp(`^${String(code || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*`, "i"), "")
            .replace(new RegExp(`\\s*${String(code || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i"), "")
            .trim() || flightNumberRaw;
          return {
            ...leg,
            airlineName,
            airlineCode: code,
            flightNumber,
          };
        }),
        airlineSummary: null,
        origin: flightOrigin,
        destination: flightDestination,
        routeLabelHe: `${formatBelvedereCampaignAirportLabel(flightOrigin)} → ${formatBelvedereCampaignAirportLabel(flightDestination)}`,
        destinationMatchesCampaign: flightsMatchCampaign,
        includedBaggageLines: includedBaggageState.includedLines || [],
        includedBaggageState: includedBaggageState.displayState,
      }
    : null;

  if (flight) {
    flight.airlineSummary =
      [
        ...new Set(
          (flight.legRows || [])
            .map((leg) => leg.airlineName)
            .filter((name) => name && name !== "—"),
        ),
      ].join(", ") || null;
  }

  const occupancyLabel = rooms.length
    ? formatCampaignSearchOccupancy(rooms)
    : null;

  const customerName = String(ci.full_name || "").trim() || null;
  const orderId = String(order.id || "").trim();

  const selectionBaggageLines = Array.isArray(selection.baggage_lines)
    ? selection.baggage_lines
        .map((line) => ({
          name: String(line?.name || "").trim(),
          priceIls: Number(line?.priceIls ?? line?.price_ils ?? 0),
          count: Number(line?.count) || 1,
        }))
        .filter((line) => line.name && Number.isFinite(line.priceIls) && line.priceIls > 0)
    : [];
  const selectionBaggageTotal = Number(selection.baggage_extras_total_ils);
  let baggageLines = selectionBaggageLines;
  let baggageExtrasTotalIls =
    Number.isFinite(selectionBaggageTotal) && selectionBaggageTotal > 0
      ? selectionBaggageTotal
      : selectionBaggageLines.reduce((sum, line) => sum + line.priceIls, 0);

  if (baggageLines.length === 0) {
    const flights = order?.flights || {};
    const fromSupplement = buildCampaignBaggageQuoteItemsFromLdsSupplement(
      flights.supplement || flights.supplements,
    );
    baggageLines = fromSupplement.lines;
    baggageExtrasTotalIls = fromSupplement.totalIls;
  }
  if (
    baggageLines.length === 0 &&
    Number(ci.baggage_extras_total_ils ?? 0) > 0
  ) {
    baggageExtrasTotalIls = Number(ci.baggage_extras_total_ils);
    baggageLines = [
      {
        name: "כבודה נוספת",
        priceIls: baggageExtrasTotalIls,
        count: 1,
      },
    ];
  }

  const resolvedBaggageExtrasIls = Number.isFinite(baggageExtrasTotalIls)
    ? baggageExtrasTotalIls
    : 0;
  const resolvedFinalSaleIls = Number.isFinite(finalSalePriceIls)
    ? finalSalePriceIls
    : 0;
  // Customer package target = final sale minus separately listed baggage.
  // Recompute even when persisted customer_passenger_pricing is a stale net copy.
  const customerPackageIls = Math.max(
    0,
    Math.round((resolvedFinalSaleIls - resolvedBaggageExtrasIls) * 100) / 100,
  );
  const customerPassengerPricing = resolveBelvedereCustomerPassengerPricing({
    customerPassengerPricing: ci.customer_passenger_pricing || null,
    passengerPricing,
    agentAddedCommissionIls,
    customerPackageIls,
  });

  const agentBranding = resolveBelvedereCampaignOfferAgentBranding(order, ci);
  const isRoyalTulip = campaignSlug === ROYAL_TULIP_CAMPAIGN_SLUG;
  const campaignPath = campaignPublicPathForSlug(campaignSlug);
  const galleryImages = isRoyalTulip
    ? ROYAL_TULIP_LOCAL_GALLERY_IMAGES.map((image) => ({
        id: image.id,
        src: image.src,
        altHe: image.altHe || summary.hotelName,
      }))
    : BELVEDERE_GALLERY_IMAGES.map((image) => ({
        id: image.id,
        src: withBelvederePublicAsset(image.src) || image.src,
        altHe: image.altHe || summary.hotelName,
      }));
  const heroImage = isRoyalTulip
    ? ROYAL_TULIP_HERO_IMAGE
    : withBelvederePublicAsset(BELVEDERE_HERO_IMAGE);
  const kicker = isRoyalTulip
    ? "קמפיין · Royal Tulip טביליסי"
    : "קמפיין · Royal Belvedere";

  return {
    orderId,
    campaignSlug,
    kicker,
    hotelName: summary.hotelName,
    hotelLocation:
      contract?.locationHe ||
      contract?.location ||
      (isRoyalTulip
        ? royalTulipTbilisiContract.locationHe
        : belvedereContract.locationHe) ||
      belvedereContract.location,
    boardLabel:
      boardEn ||
      selectedRoom.boardHe ||
      contract?.boardHe ||
      contract?.board ||
      (isRoyalTulip ? royalTulipTbilisiContract.boardHe : belvedereContract.boardHe) ||
      belvedereContract.board,
    roomName: roomName ? stripCampaignBoardFromRoomName(roomName, roomName) : roomName,
    roomImage,
    roomDetails,
    heroImage,
    galleryImages,
    arrivalDate,
    departureDate,
    checkInLabel: summary.checkInLabel,
    checkOutLabel: summary.checkOutLabel,
    nights,
    rooms,
    occupancyLabel,
    productType,
    hasFlights: summary.hasFlights,
    flight,
    flightOrigin,
    passengerPricing,
    customerPassengerPricing,
    baggageLines,
    baggageExtrasTotalIls: resolvedBaggageExtrasIls,
    finalSalePriceIls: resolvedFinalSaleIls,
    agentNetPriceIls: Number.isFinite(agentNetPriceIls) ? agentNetPriceIls : 0,
    agentAddedCommissionIls: Number.isFinite(agentAddedCommissionIls)
      ? agentAddedCommissionIls
      : 0,
    agentBranding,
    customerName,
    paymentPath: orderId ? buildOfferPaymentPath(orderId, campaignPath) : null,
    campaignPath,
  };
}

export async function fetchBelvedereCampaignDynamicOrder(orderId) {
  const oid = String(orderId || "").trim();
  if (!oid) return null;

  const user = getUserDetails();
  const agentId = resolveSessionAgentId(user);
  const res = await getDynamicOrder({
    id: oid,
    agentID: agentId ?? undefined,
    isAdmin: isAdminUser() ? 1 : 0,
  });
  const data = res?.data;
  if (!data || typeof data !== "object" || !data.id) return null;
  return data;
}
